import React from 'react'
import Image, { StaticImageData } from 'next/image'
import useTranslation from 'next-translate/useTranslation'
import { Text } from 'components/Text'
import { SectionTitle } from '../../AllProducts.styles'
import {
  ProductCatalogRoot,
  ProductsGrid,
  ProductCard,
  ProductImage,
  ProductTitleWrapper,
  ProductCta,
  ProductDescriptionWrapper,
  ProductContent,
  Spacer,
  BorderWrapper,
  Blob1Image,
  Blob2Image,
} from './ProductCatalog.styles'
import customMealPlansImage from '../../../../../../../public/assets/product/custom-meal-plans.png'
import bulkPattiesImage from '../../../../../../../public/assets/product/bulk-patties.png'
import Box from '../../../../../../../public/assets/recipes/categories/we-feed-raw-box.png'
import blob1 from '../../../../../../../public/assets/recipes/categories/blob7.png'
import blob2 from '../../../../../../../public/assets/recipes/categories/blob8.png'
import { BorderWave } from 'components/BorderWave'
import { BorderWaveOutline } from 'components/BorderWaveOutline/BorderWaveOutline'
import Link from 'next/link'
import { Button } from 'components/Button'

export type ProductCardType = {
  title: string
  image: StaticImageData
  imageAlt: string
  cta: string
  ctaMobile: string
  description: string
  variant: 'default' | 'bulk'
  link: string
}

export const ProductCatalog: React.FC = () => {
  const { t } = useTranslation()

  const products: ProductCardType[] = [
    {
      title: 'products:allProducts.customMealPlans.title',
      image: customMealPlansImage,
      imageAlt: 'products:allProducts.customMealPlans.imageAlt',
      cta: 'products:allProducts.customMealPlans.cta',
      ctaMobile: 'products:allProducts.customMealPlans.cta',
      description: 'products:allProducts.customMealPlans.description',
      variant: 'default',
      link: '/meal-plan',
    },
    {
      title: 'products:allProducts.essentialBox.title',
      image: Box,
      imageAlt: 'products:allProducts.essentialBox.imageAlt',
      cta: 'products:allProducts.essentialBox.cta',
      ctaMobile: 'products:allProducts.essentialBox.cta-m',
      description: 'products:allProducts.essentialBox.description',
      variant: 'default',
      link: '/products/classic-box',
    },
    {
      title: 'products:allProducts.bulkPatties.title',
      image: bulkPattiesImage,
      imageAlt: 'products:allProducts.bulkPatties.imageAlt',
      cta: 'products:allProducts.bulkPatties.cta',
      ctaMobile: 'products:allProducts.bulkPatties.cta-m',
      description: 'products:allProducts.bulkPatties.description',
      variant: 'bulk',
      link: '/products/5-lb-beef-patty-recipe',
    },
  ]

  const Product = ({
    title,
    image,
    imageAlt,
    cta,
    description,
    link,
    variant,
    ctaMobile,
  }: ProductCardType) => (
    <ProductContent>
      <ProductCard>
        <ProductTitleWrapper>
          <Text size='bodyExtraLarge' weight="medium" color="quaternary-200">
            {t(title)}
          </Text>
        </ProductTitleWrapper>
        <ProductImage>
          <Image layout='fill' objectFit='cover' src={image} alt={t(imageAlt)} />
        </ProductImage>
        <ProductCta className="desktop-only">
          <Link  href={link}>
            <Button>
              {t(cta)}
            </Button>
          </Link>
        </ProductCta>
        <ProductCta className="mobile-only" >
           <Link href={link}>
            <Button>
              {t(ctaMobile)}
            </Button>
          </Link>
        </ProductCta>
       
      </ProductCard>
      <ProductDescriptionWrapper variant={variant}>
        <Text size={{ '@initial': 'body', '@bp2': 'bodyLarge' }} color="quaternary-200">
          {t(description)}
        </Text>
      </ProductDescriptionWrapper>
    </ProductContent>
  )

  return (
    <>
      <ProductCatalogRoot>
        <SectionTitle align="center">{t('products:allProducts.title-1')}</SectionTitle>
        <ProductsGrid>
          {products.map((product, index) => (
            <Product
              key={index}
              title={product.title}
              description={product.description}
              cta={product.cta}
              image={product.image}
              imageAlt={product.imageAlt}
              link={product.link}
              variant={product.variant}
              ctaMobile={product.ctaMobile}
            />
          ))}
        </ProductsGrid>
        <Spacer />
        <Blob1Image>
          <Image src={blob1} alt="" />
        </Blob1Image>
        <Blob2Image>
          <Image src={blob2} alt="" />
        </Blob2Image>
      </ProductCatalogRoot>

      <BorderWrapper>
        <BorderWave
          background="secondary-100"
          svgWidth="calc(169% + 1.3px)"
          svgHeight="74px"
          bottom
          path="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
        />
        <BorderWaveOutline
          strokeColor="primary-500"
          strokeWidth={12}
          svgHeight="74px"
          svgWidth="calc(169% + 1.3px)"
          path="M0,27.35A600.21,600.21,0,0,0,321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3"
          bottom
        />
      </BorderWrapper>
    </>
  )
}